/**
 * Type-safe wrapper around the Emscripten WASM module
 * Each instance represents an independent simulation
 *
 * Note: The Emscripten-generated module is a singleton per load.
 * To support multiple independent simulations, we load each instance
 * via an iframe or use a single shared module with manual state management.
 *
 * For this implementation, we use a single shared module - which means
 * only one active simulation at a time actually runs the WASM step.
 * This is a trade-off for simplicity until WASM module isolation is needed.
 */

import type { EmscriptenModule, SimulationAPI } from '../types/wasm';

// Global module reference (singleton pattern for Emscripten)
let globalModule: EmscriptenModule | null = null;
let moduleLoadPromise: Promise<EmscriptenModule> | null = null;
let instanceCount = 0;

export class WasmModule {
  private module: EmscriptenModule;
  private _api: SimulationAPI | null = null;
  private positionBuffer: number = 0;
  private readonly maxEntities: number;
  private readonly instanceId: number;
  private disposed: boolean = false;

  private constructor(module: EmscriptenModule, maxEntities: number = 10000) {
    this.module = module;
    this.maxEntities = maxEntities;
    this.instanceId = instanceCount++;

    // Allocate position buffer in WASM memory (3 floats per entity)
    this.positionBuffer = module._malloc(maxEntities * 3 * 4);
  }

  /**
   * Load or get the shared WASM module instance
   * Note: This is a shared module - all WasmModule instances share the same WASM state
   */
  static async load(maxEntities: number = 10000): Promise<WasmModule> {
    const module = await WasmModule.getSharedModule();
    return new WasmModule(module, maxEntities);
  }

  /**
   * Get or load the shared Emscripten module
   */
  private static async getSharedModule(): Promise<EmscriptenModule> {
    if (globalModule) {
      return globalModule;
    }

    if (moduleLoadPromise) {
      return moduleLoadPromise;
    }

    moduleLoadPromise = new Promise((resolve, reject) => {
      // Check if Module is already defined (script already loaded)
      const existingModule = (window as unknown as { Module?: EmscriptenModule }).Module;
      if (existingModule && typeof existingModule.cwrap === 'function') {
        globalModule = existingModule;
        resolve(existingModule);
        return;
      }

      // Load the script
      const script = document.createElement('script');
      script.src = '/public/computein.js';
      script.async = true;

      script.onload = () => {
        // Poll for Module to be ready (Emscripten may still be initializing)
        const checkModule = () => {
          const mod = (window as unknown as { Module?: EmscriptenModule }).Module;
          if (mod && typeof mod.cwrap === 'function' && mod.HEAPF32) {
            globalModule = mod;
            resolve(mod);
          } else if (mod) {
            // Module exists but not fully initialized - wait for onRuntimeInitialized
            const originalOnRuntimeInitialized = mod.onRuntimeInitialized;
            mod.onRuntimeInitialized = () => {
              if (originalOnRuntimeInitialized) {
                originalOnRuntimeInitialized.call(mod);
              }
              globalModule = mod;
              resolve(mod);
            };
          } else {
            // Keep polling
            setTimeout(checkModule, 10);
          }
        };
        checkModule();
      };

      script.onerror = () => {
        moduleLoadPromise = null;
        reject(new Error('Failed to load computein.js'));
      };

      document.head.appendChild(script);
    });

    return moduleLoadPromise;
  }

  /**
   * Get the typed simulation API
   */
  get api(): SimulationAPI {
    if (this.disposed) {
      throw new Error('WasmModule has been disposed');
    }
    if (!this._api) {
      this._api = this.createAPI();
    }
    return this._api;
  }

  /**
   * Create typed wrappers for all WASM exports
   */
  private createAPI(): SimulationAPI {
    const cwrap = this.module.cwrap.bind(this.module);

    return {
      // Entity management
      createEntity: cwrap('sim_create_entity', 'number', []) as () => number,
      destroyEntity: cwrap('sim_destroy_entity', null, ['number']) as (
        e: number
      ) => void,
      entityCount: cwrap('sim_entity_count', 'number', []) as () => number,

      // Component setters
      setPosition: cwrap('sim_set_position', null, [
        'number',
        'number',
        'number',
        'number',
      ]) as (e: number, x: number, y: number, z: number) => void,
      setVelocity: cwrap('sim_set_velocity', null, [
        'number',
        'number',
        'number',
        'number',
      ]) as (e: number, vx: number, vy: number, vz: number) => void,
      setPhysicalProps: cwrap('sim_set_physical_props', null, [
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
      ]) as (
        e: number,
        mass: number,
        radius: number,
        charge: number,
        hydrophobicity: number,
        diffusionCoeff: number,
        elasticity: number
      ) => void,
      setMoleculeType: cwrap('sim_set_molecule_type', null, [
        'number',
        'number',
        'number',
      ]) as (e: number, archetypeId: number, variantFlags: number) => void,
      setTags: cwrap('sim_set_tags', null, ['number', 'number']) as (
        e: number,
        flags: number
      ) => void,
      setLOD: cwrap('sim_set_lod', null, ['number', 'number']) as (
        e: number,
        lod: number
      ) => void,
      setBindingSites: cwrap('sim_set_binding_sites', null, [
        'number',
        'number',
      ]) as (e: number, siteCount: number) => void,
      setStateMachine: cwrap('sim_set_state_machine', null, [
        'number',
        'number',
      ]) as (e: number, definitionId: number) => void,
      setLifetime: cwrap('sim_set_lifetime', null, ['number', 'number']) as (
        e: number,
        halfLife: number
      ) => void,

      // Simulation control
      step: cwrap('sim_step', null, []) as () => void,
      pause: cwrap('sim_pause', null, []) as () => void,
      resume: cwrap('sim_resume', null, []) as () => void,
      isPaused: cwrap('sim_is_paused', 'number', []) as () => number,
      setDt: cwrap('sim_set_dt', null, ['number']) as (dt: number) => void,
      setTemperature: cwrap('sim_set_temperature', null, ['number']) as (
        temp: number
      ) => void,
      getTick: cwrap('sim_get_tick', 'number', []) as () => number,
      isInitialized: cwrap('sim_is_initialized', 'number', []) as () => number,

      // Batch operations
      spawnRandom: cwrap('sim_spawn_random', null, [
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
      ]) as (
        count: number,
        archetypeId: number,
        mass: number,
        radius: number,
        charge: number,
        hydrophobicity: number,
        diffusionCoeff: number,
        boundsMin: number,
        boundsMax: number
      ) => void,

      // World configuration
      setWorldBounds: cwrap('sim_set_world_bounds', null, [
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
      ]) as (
        minX: number,
        minY: number,
        minZ: number,
        maxX: number,
        maxY: number,
        maxZ: number,
        cellSize: number
      ) => void,

      // Readback
      getEntityCount: cwrap('sim_get_entity_count', 'number', []) as () => number,
      getDt: cwrap('sim_get_dt', 'number', []) as () => number,
      getTemperature: cwrap('sim_get_temperature', 'number', []) as () => number,

      // DNA Compiler
      compileDNA: cwrap('sim_compile_dna', 'string', ['string']) as (
        sequence: string
      ) => string,

      // Metrics
      getMetrics: cwrap('sim_get_metrics', 'string', []) as () => string,

      // State save/load
      saveState: cwrap('sim_save_state', 'string', []) as () => string,
    };
  }

  /**
   * Get all entity positions as Float32Array
   * Returns copy of data (safe to use across frames)
   */
  getPositions(): { positions: Float32Array; count: number } {
    if (this.disposed) {
      return { positions: new Float32Array(0), count: 0 };
    }

    const entityCount = Math.min(this.api.getEntityCount(), this.maxEntities);

    if (entityCount === 0) {
      return { positions: new Float32Array(0), count: 0 };
    }

    // Call WASM to fill position buffer
    const written = this.module._sim_get_all_positions(
      this.positionBuffer,
      entityCount
    );

    if (written === 0) {
      return { positions: new Float32Array(0), count: 0 };
    }

    // Create a copy of the data from WASM memory
    const positions = new Float32Array(
      this.module.HEAPF32.buffer,
      this.positionBuffer,
      written * 3
    ).slice();

    return { positions, count: written };
  }

  /**
   * Get direct access to WASM memory for positions
   * WARNING: This view is only valid until next WASM call
   */
  getPositionsView(): { positions: Float32Array; count: number } {
    if (this.disposed) {
      return { positions: new Float32Array(0), count: 0 };
    }

    const entityCount = Math.min(this.api.getEntityCount(), this.maxEntities);

    if (entityCount === 0) {
      return { positions: new Float32Array(0), count: 0 };
    }

    const written = this.module._sim_get_all_positions(
      this.positionBuffer,
      entityCount
    );

    if (written === 0) {
      return { positions: new Float32Array(0), count: 0 };
    }

    // Return view into WASM memory (no copy)
    const positions = new Float32Array(
      this.module.HEAPF32.buffer,
      this.positionBuffer,
      written * 3
    );

    return { positions, count: written };
  }

  /**
   * Clean up WASM resources
   */
  dispose(): void {
    if (this.disposed) {
      return;
    }

    if (this.positionBuffer) {
      this.module._free(this.positionBuffer);
      this.positionBuffer = 0;
    }
    this._api = null;
    this.disposed = true;
  }
}
